'use client';

import React, { useState } from 'react';

import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import { useTranslations } from 'next-intl';

import { SUGGESTED_FOR_YOU_MAX_ITEMS } from './constants';
import styles from './styles.module.css';

import { HomepageProductCard } from '@/app/[locale]/(home)/components/HomepageProductCard';
import { SuggestedForYouModal } from '@/app/[locale]/(home)/components/SuggestedForYou/SuggestedForYouModal';
import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useSuggestedForYouProducts } from '@/modules/explore/hooks/useSuggestedForYouProducts';
import { getProductCardColumns } from '@/modules/recentlyViewed/helpers';
import { getProductSizeName } from '@/modules/sizes/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function SuggestedForYouUi() {
  const { data } = useSuggestedForYouProducts();
  const tCommon = useTranslations('common');
  const tHome = useTranslations('home');
  const { isMaxMedium } = useResponsive();
  const [_, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const products = data.slice(0, SUGGESTED_FOR_YOU_MAX_ITEMS);
  if (products.length === 0) {
    return null;
  }

  function handleProductClick(
    selectedProductId: number,
    selectedProductName: string,
    selectedProductPosition: number
  ) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.SuggestedForYou,
      itemClicked: selectedProductName,
      itemId: String(selectedProductId),
      itemPosition: selectedProductPosition,
      schemaVersion: '3.0',
    });
  }

  function handleSeeMoreClick() {
    setIsModalOpen(true);
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName: HomePageModuleNames.SuggestedForYou,
        interactionType: ModuleInteractionType.SeeMoreClicked,
        schemaVersion: '1.0',
      }
    );
  }

  return (
    <section>
      <ModuleHeader
        title={tHome('SuggestedForYou')}
        size={ModuleHeaderSize.Large}
      >
        <ButtonMinimal
          className={styles.seeMoreText}
          onClick={handleSeeMoreClick}
        >
          {tCommon('SeeMore')}
        </ButtonMinimal>
      </ModuleHeader>

      <ScrollableItemRow
        minItemWidth={144}
        itemsPerRow={
          !isMaxMedium
            ? getProductCardColumns(products.length)
            : products.length
        }
      >
        {products.map((product, index) => (
          <li key={product.id}>
            <HomepageProductCard
              id={product.id}
              isLiked={product.is_liked}
              isBoosted={product.is_boosted}
              preview={product.preview}
              productHref={`${product.slug}?moduleOrigin=${HomePageModuleNames.SuggestedForYou}`}
              pictures={product.pictures}
              displayPrice={product.pricing.display_price}
              hasVideo={product.has_video}
              size={
                product.sizes &&
                getProductSizeName(product.sizes, {
                  multipleSizes: tCommon('MultipleSizes'),
                })
              }
              handleClick={() =>
                handleProductClick(product.id, product.slug, index)
              }
              brandName={product.brand_name}
            />
          </li>
        ))}
      </ScrollableItemRow>

      <SuggestedForYouModal
        isOpen={isModalOpen}
        setOpen={setIsModalOpen}
        onProductClick={handleProductClick}
      />
    </section>
  );
}
